import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Button, Grid, IconButton } from "@mui/material";
import { Dispatch, MutableRefObject, SetStateAction } from "react";
import SignaturePad from "signature_pad";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import ErrorHelper from "../../../../../components/ErrorWrapper";
import Text from "../../../../../components/Text";
import SignatureInput from "../../../../../components/signiture/SignatureInput";

const agreementSections = [
  {
    title: "Medical Consent:",
    content:
      "I consent to any treatments or procedures which may be performed on an outpatient basis (including emergency treatment or services), which may include but are not limited to medications, injections, taking of medical photographs, laboratory procedures, and/or x-ray examinations provided to me under the general and special instructions of the physicians, staff, or other health care providers of The Medical Dock assisting my care.",
  },
  {
    title:
      "Financial Agreement: I understand that all charges are due at the time of service.",
    content:
      "I agree to pay The Medical Dock for all charges for healthcare services and professional services provided to me by physicians and other healthcare professionals. Acceptable forms of payment include Cash, Visa, MasterCard, Discover, and American Express. If I am a non-insured patient, I agree to pay for my visit in full at the time of service. If The Medical Dock is a participating provider with my insurance company, I understand that my co-pay, coinsurance, deductible, and/or any outstanding balances are due at the time of service. I understand that my insurance policy is a contract between myself and my insurance company, The Medical Dock is not involved. In order for The Medical Dock to file claims and accept payments from my insurance carrier, I understand that I must present current insurance information at each visit and that The Medical Dock will need to verify my health insurance coverage. In the event that The Medical Dock is not able to verify my insurance eligibility and benefits before my visit, I agree to pay for my visit in full at the time of service. A refund will be issued if my insurance pays for the visit. I also understand that I am financially responsible for any services not covered by my insurance company. When my spouse or a financial guarantor signs this agreement, the spouse or the financial guarantor shall be jointly and individual liable with me. Should my account(s) be referred to an attorney or a collection agency for the collection, the undersigned shall pay the actual attorney's fees (including costs) and collections expenses incurred in addition to the other amounts due. Unpaid accounts referred to outside agencies for collection shall bear interest at the current rate per year from the date of referral.",
  },
  {
    title: "Insurance Authorization and Release:",
    content:
      "I request the payment of authorized benefits, including Medicare, and any other government sponsored program, private insurance, and any other health plans to be made to The Medical Dock for any services furnished by that provider. To the extent necessary to coordinate my health care or determine liability for payment and to obtain reimbursement for services rendered, I authorize The Medical Dock to disclose portions of or all of my records, including my medical records to any person or corporation which is or may be liable for all or any portion of The Medical Dock charges, including but not limited to insurance companies, health care service plans, governmental agencies, or worker's compensation carriers. I authorize The Medical Dock to act as my agent to help obtain any required pre-certification as well as acting as my agent to help me obtain payment from my insurance companies. I authorize my insurance companies to give The Medical Dock any information required to fulfill this function. This will remain in effect until revoked in writing. A photocopy of this assignment and release is to be considered as valid as the original.",
  },
  {
    title: "Release of Medical Information:",
    content:
      "I hereby authorize The Medical Dock to release any information in my chart to any practitioner, doctor, hospital, or medical institution to which I may be referred to assist in my care. Additionally, I authorize The Medical Dock to provide a copy of my medical records to my Primary Care Physician (PCP) to allow for continuity of care.",
  },
  {
    title: "Notice of Privacy Practices:",
    content:
      "By signing this form, you acknowledge receipt of the 'Notice Of Privacy Practices' of The Medical Dock. Our 'Notice of Privacy Practices' provides information about how we may use and disclose your protected health information. We encourage you to read it in full. Our 'Notice of Privacy Practices' is subject to change. If we change our notice, you may obtain a copy of the revised notice by contacting The Medical Dock at (714)596-0400.",
  },
  {
    title: "In House Pharmacy:",
    content:
      "I understand that, for my convenience, The Medical Dock can dispense some prescription medications necessary to treat my medical condition(s). I understand that my insurance will not be billed for medications dispensed and that my pharmacy benefits DO NOT apply to this service. Any medication(s) dispensed in the office are my responsibility and are an additional charge to my office visit charge. I also understand that if I prefer to use an outside pharmacy, a prescription can be provided to me at no additional charge.",
  },
  {
    title: "Personal Valuables:",
    content:
      "The Medical Dock shall not be liable for the loss of or damage to any money, documents, jewelry, glasses, dentures, furs, or other articles of unusual value and shall not be liable for loss or damage to any personal property. The Medical Dock, A medical corporation and the patient or the patient's representative, hereby enters into this agreement. The undersigned certifies that he/she has read and agree to the foregoing, and is the patient, the patient's representative or is duly authorized by the patient as the patient's general agent to execute the above and accept its terms.",
  },
];

const MedicalServicesAgreementForm = ({
  viewOnly,
  setFieldValue,
  values,
  canvasRef,
  signPad,
  isSignEmpty,
  setK,
  errors: _,
}: {
  viewOnly?: boolean;
  setFieldValue: (field: string, value: any) => void;
  values: any;
  canvasRef: MutableRefObject<HTMLCanvasElement | null>;
  signPad: SignaturePad | undefined;
  isSignEmpty: () => boolean;
  setK: Dispatch<SetStateAction<boolean>>;
  errors: any;
}) => {
  return (
    <Box maxWidth={800} mx="auto">
      <Text variant="h5" fontWeight="bold" align="center" gutterBottom>
        MEDICAL SERVICES AGREEMENT
      </Text>
      {agreementSections.map((section, idx) => (
        <Box key={idx} mb={2}>
          <Text variant="subtitle1" fontWeight="bold" gutterBottom>
            {section.title}
          </Text>
          <Text variant="body2">{section.content}</Text>
        </Box>
      ))}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={6} lg={12}>
          <CustomDatePicker
            disabled={viewOnly}
            label="Date"
            value={values?.medicalServicesAgreementDate}
            onChange={(val) => {
              setFieldValue(`medicalServicesAgreementDate`, val);
            }}
          />
        </Grid>
        <Grid item container mt={1}>
          {viewOnly || values.medicalServicesAgreementSign ? (
            <Box>
              <ErrorHelper label={"Signature"}>
                <Box
                  style={{
                    position: "relative",
                    borderWidth: 1,
                    borderRadius: 5,
                    borderStyle: "solid",
                    height: "100%",
                  }}
                >
                  <img
                    src={values.medicalServicesAgreementSign}
                    style={{ height: "150px", width: "368px" }}
                  />

                  {!viewOnly && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        setK((prev) => !prev);
                        signPad?.clear();
                        setFieldValue(
                          "medicalServicesAgreementSign",
                          undefined
                        );
                      }}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -20,
                        height: "10px",
                      }}
                    >
                      <HighlightOffIcon color="error" fontSize="medium" />
                    </IconButton>
                  )}
                </Box>
              </ErrorHelper>
            </Box>
          ) : (
            // <ErrorHelper error={errors?.medicalServicesAgreementSign}>
            <Box
              width={"368px"}
              position={"relative"}
              onMouseLeave={() => {
                if (!isSignEmpty() && signPad?.toDataURL()) {
                  setFieldValue(
                    "medicalServicesAgreementSign",
                    signPad?.toDataURL()
                  );
                }
              }}
            >
              <SignatureInput
                // required
                // error={submitCount > 0 && isSignEmpty()}
                label="Signature"
                canvasRef={canvasRef}
                w="368px"
                h="100px"
              />

              <Button
                variant="contained"
                sx={{
                  position: "absolute",
                  bottom: 10,
                  right: 10,
                  height: "24px",
                }}
                onClick={() => {
                  setK((prev: boolean) => !prev);
                  setFieldValue("medicalServicesAgreementSign", undefined);
                  signPad?.clear();
                }}
              >
                clear
              </Button>
            </Box>
            // </ErrorHelper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MedicalServicesAgreementForm;
